import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { isFuture, isPast } from 'date-fns';
import { useCallback } from 'react';

import { Orb } from '@sorare/core/src/components/Orb';
import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs/index.tsx';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';
import { useOpenDialog } from '@sorare/core/src/hooks/navigation/useOpenDialog.ts';
import { useIsVisibleInViewport } from '@sorare/core/src/hooks/ui/useIsVisibleInViewport.ts';
import {
  LIFECYCLE,
  useLifecycle,
} from '@sorare/core/src/hooks/useLifecycle.ts';
import { useCommonGameSet } from '@sorare/core/src/set';

import type { WelcomePackOrb_availableVoucher } from './__generated__/index.graphql.ts';

type Props = {
  availableVoucher: WelcomePackOrb_availableVoucher;
};

export const WelcomePackOrb = ({ availableVoucher }: Props) => {
  const commonGameSet = useCommonGameSet();
  const { id, endDate } = availableVoucher;
  const openDialog = useOpenDialog();
  const { lifecycle } = useLifecycle();

  const handleFirstVisible = useCallback(() => {
    if (!endDate) {
      return;
    }
    const nextShowDate = lifecycle?.[LIFECYCLE.welcomePackAutoOpen]?.[id];
    if (!nextShowDate || (isPast(nextShowDate) && isFuture(endDate))) {
      openDialog(DialogKey.welcomePack, 'true');
    }
  }, [id, lifecycle, openDialog, endDate]);

  const { handleRef } = useIsVisibleInViewport<HTMLDivElement>({
    callback: handleFirstVisible,
  });

  return (
    <div ref={handleRef}>
      <Orb
        label={!!endDate && <TimeLeft time={endDate} />}
        chipState={endDate ? 'timeLeft' : undefined}
        colors={[0, 243, 69]}
        showNotificationDot={false}
        onClick={() => openDialog(DialogKey.welcomePack, 'true')}
      >
        <img
          src={commonGameSet.visual.play.voucherIcon}
          alt="Voucher"
          height={50}
        />
      </Orb>
    </div>
  );
};

WelcomePackOrb.fragments = {
  availableVoucher: gql`
    fragment WelcomePackOrb_availableVoucher on PurchasableItemsVoucher {
      id
      endDate
    }
  ` as TypedDocumentNode<WelcomePackOrb_availableVoucher>,
};
